import { DatePipe, DecimalPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	OnDestroy,
	OnInit,
	computed,
	inject,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { PricePipe } from '@/common/pipes/price.pipe';
import { FlightGridApiService } from '@/flight-grid/flight-grid.api.service';
import { FlightGridSearchStatus, FlightTicketPriceResult } from '@/flight-grid/flight-grid.model';
import { EMPTY, Subscription, catchError, interval, startWith, switchMap } from 'rxjs';
import {
	CheckTicketPricesDialogData,
	getPurchasedTicketsForFlight,
	getUnavailablePurchasedTickets,
	groupPurchasedTickets,
} from './_utils/purchased-ticket-groups';
import {
	findTicketPriceSearchCacheEntry,
	removeTicketPriceSearchCacheEntry,
	saveTicketPriceSearchId,
	saveTicketPriceSearchResults,
} from './_utils/ticket-price-search-cache';

@Component({
	selector: 'app-check-ticket-prices-dialog',
	standalone: true,
	imports: [MatDialogModule, MatButtonModule, MatCardModule, MatProgressBarModule, DatePipe, DecimalPipe, PricePipe],
	templateUrl: './check-ticket-prices-dialog.component.html',
	styleUrl: './check-ticket-prices-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckTicketPricesDialogComponent implements OnInit, OnDestroy {
	readonly data = inject<CheckTicketPricesDialogData>(MAT_DIALOG_DATA);
	readonly progressPercent = signal(0);
	readonly isLoading = signal(true);
	readonly results = signal<FlightTicketPriceResult[]>([]);
	readonly errorMessage = signal<string | null>(null);
	readonly flightCards = computed(() =>
		this.results().map((flight) => ({
			flight,
			priceWithMargin: flight.price + flight.flightPriceMarginPln,
			purchasedTickets: getPurchasedTicketsForFlight(this.purchasedTicketGroups, flight),
		})),
	);
	readonly unavailablePurchasedTickets = computed(() =>
		getUnavailablePurchasedTickets(this.purchasedTicketGroups, this.results()),
	);

	private readonly flightGridApiService = inject(FlightGridApiService);
	private readonly infoboxService = inject(InfoboxService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly purchasedTicketGroups = groupPurchasedTickets(this.data?.purchasedTickets ?? []);
	private searchProgressSubscription: Subscription | null = null;

	ngOnInit(): void {
		if (!this.data?.reservationIds?.length) {
			this.showError('Brak rezerwacji do sprawdzenia cen biletów');
			return;
		}

		const cachedEntry = findTicketPriceSearchCacheEntry(this.data.reservationIds);
		if (cachedEntry?.results) {
			this.results.set(cachedEntry.results);
			this.isLoading.set(false);
			return;
		}

		if (cachedEntry?.searchId) {
			this.startProgressPolling(cachedEntry.searchId);
			return;
		}

		this.startSearch();
	}

	ngOnDestroy(): void {
		this.stopProgressPolling();
	}

	private startSearch(): void {
		this.flightGridApiService
			.searchByReservations({ reservationIds: this.data.reservationIds })
			.pipe(
				catchError(() => {
					this.showError('Wystąpił błąd podczas uruchamiania wyszukiwania cen biletów', false);
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((response) => {
				saveTicketPriceSearchId(this.data.reservationIds, response.searchId);
				this.startProgressPolling(response.searchId);
			});
	}

	private startProgressPolling(searchId: number): void {
		this.stopProgressPolling();
		this.isLoading.set(true);
		this.progressPercent.set(0);
		this.searchProgressSubscription = interval(2000)
			.pipe(
				startWith(0),
				switchMap(() => this.flightGridApiService.getFlightGridSearchProgress(searchId)),
				catchError(() => {
					this.stopProgressPolling();
					this.showError('Wystąpił błąd podczas sprawdzania postępu wyszukiwania', false);
					return EMPTY;
				}),
			)
			.subscribe((progressResponse) => {
				this.progressPercent.set(progressResponse.percent);

				if (progressResponse.status === FlightGridSearchStatus.STATUS_DONE) {
					this.stopProgressPolling();
					this.loadResults(searchId);
					return;
				}

				if (progressResponse.status === FlightGridSearchStatus.STATUS_ERROR) {
					this.stopProgressPolling();
					removeTicketPriceSearchCacheEntry(this.data.reservationIds);
					this.showError(progressResponse.error ?? 'Wystąpił błąd podczas wyszukiwania cen biletów');
				}
			});
	}

	private loadResults(searchId: number): void {
		this.flightGridApiService
			.getFlightSearchResults(searchId)
			.pipe(
				catchError(() => {
					this.showError('Wystąpił błąd podczas pobierania wyników wyszukiwania', false);
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((results) => {
				const nextResults = results ?? [];
				saveTicketPriceSearchResults(this.data.reservationIds, nextResults);
				this.results.set(nextResults);
				this.isLoading.set(false);
			});
	}

	private showError(message: string, notify = true): void {
		this.isLoading.set(false);
		this.errorMessage.set(message);
		if (notify) {
			this.infoboxService.error(message);
		}
	}

	private stopProgressPolling(): void {
		this.searchProgressSubscription?.unsubscribe();
		this.searchProgressSubscription = null;
	}
}
